import type {
  Account,
  CheckoutChoice,
  Menu,
  MenuSetting,
  Plan,
  PlansOpener,
  Usage,
} from '@lyrmo/shared';
import {
  useCallback,
  useEffect,
  useEffectEvent,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { ApiClient } from '../../api/client';
import { useI18n } from '../../i18n/i18n-context';
import { useTrack } from '../../lib/tracking-context';
import { withSetting } from '../rewrite/menu-items';
import { useAiTools } from '../rewrite/useAiTools';
import { useCustomTones } from '../rewrite/useCustomTones';
import { useMenuSettings } from '../rewrite/useMenuSettings';
import { useSavedInstructions } from '../rewrite/useSavedInstructions';
import {
  AccountContext,
  type AccountContextValue,
  type AccountDialog,
  type InstructionDraft,
  type Notice,
} from './account-context';
import { clearArrival, readArrival } from './arrival';
import { TRANSACTION_PARAM, openPaddleCheckout, withoutTransaction } from './paddle-checkout';

// The payment provider's webhook can land a few seconds after checkout, so check the plan again.
const RECHECK_PLAN_AFTER_MS = [2_000, 5_000, 10_000];
// Refreshing just after the daily reset shows the new allowance.
const RESET_GRACE_MS = 5_000;

const leave = (url: string) => {
  window.location.assign(url);
};

interface AccountProviderProps {
  readonly client: ApiClient;
  /** Leaves the app for checkout. Injectable for tests. */
  readonly navigate?: (url: string) => void;
  /** Opens Paddle's checkout for the transaction in the address. Injectable for tests. */
  readonly openCheckout?: typeof openPaddleCheckout;
  readonly children: ReactNode;
}

/** Holds the viewer's plan and usage, the account dialogs, and account notices. */
export function AccountProvider({
  client,
  navigate = leave,
  openCheckout = openPaddleCheckout,
  children,
}: AccountProviderProps) {
  const { t, describeError } = useI18n();
  const [arrival] = useState(() => readArrival(window.location.search, t));
  // Paddle's checkout links come here with the transaction to pay.
  const [transaction] = useState(() =>
    new URLSearchParams(window.location.search).get(TRANSACTION_PARAM),
  );
  const [account, setAccount] = useState<Account | null>(null);
  const [plans, setPlans] = useState<Plan[] | null>(null);
  const [planChangesEnabled, setPlanChangesEnabled] = useState(false);
  const [plansError, setPlansError] = useState<string | null>(null);
  // The pricing page shows the plans without their dialog.
  const [plansWanted, setPlansWanted] = useState(false);
  const [dialog, setDialog] = useState<AccountDialog | null>(arrival.dialog);
  const [notice, setNotice] = useState<Notice | null>(arrival.notice);
  const track = useTrack();
  // A link that opens the plans, such as the desktop app's, opened them.
  const arrivedAtPlans = useRef(arrival.dialog === 'plans');
  useEffect(() => {
    if (!arrivedAtPlans.current) return;
    arrivedAtPlans.current = false;
    track({ event: 'plans', from: 'link' });
  }, [track]);
  const owner = account ? (account.user?.email ?? null) : undefined;
  const customTones = useCustomTones(client, owner);
  const savedInstructions = useSavedInstructions(client, owner);
  const [instructionDraft, setInstructionDraft] = useState<InstructionDraft | null>(null);
  const aiTools = useAiTools(client, owner);
  const [newAiTool, setNewAiTool] = useState(false);
  const [newTone, setNewTone] = useState(false);
  const toolSettings = useMenuSettings(client, owner, 'tools');
  const toneSettings = useMenuSettings(client, owner, 'tones');
  const instructionSettings = useMenuSettings(client, owner, 'instructions');
  const settingLists = {
    tools: toolSettings,
    tones: toneSettings,
    instructions: instructionSettings,
  };
  const settingListsRef = useRef(settingLists);
  useLayoutEffect(() => {
    settingListsRef.current = settingLists;
  });

  const reloadMenuSettings = useCallback(
    (menu: Menu) => settingListsRef.current[menu].reload(),
    [],
  );

  const keepMenuItem = useCallback(
    async (menu: Menu, setting: MenuSetting) => {
      const list = settingListsRef.current[menu];
      list.patch((settings) => withSetting(settings, setting));
      try {
        await client.updateMenuSetting(menu, setting.id, {
          hidden: setting.hidden,
          pinned: setting.pinned,
        });
      } catch (error) {
        // Back to what the server has, before saying it failed.
        await list.reload().catch(() => undefined);
        throw error;
      }
    },
    [client],
  );

  const refresh = useCallback(
    () =>
      client.getAccount().then(setAccount, () => {
        // Keep what we have; the account bar stays as it was.
      }),
    [client],
  );

  const applyUsage = useCallback((usage: Usage) => {
    setAccount((current) => current && { ...current, usage });
  }, []);

  const checkout = useCallback(
    async (choice: CheckoutChoice) => {
      navigate(await client.startCheckout(choice));
    },
    [client, navigate],
  );

  const changePlan = useCallback(
    async (choice: CheckoutChoice) => {
      await client.changePlan(choice);
      await refresh();
      setNotice({ tone: 'success', text: t.notices.planChanged(t.planNames[choice.plan]) });
    },
    [client, refresh, t],
  );

  const cancelSubscription = useCallback(async () => {
    await client.cancelSubscription();
    await refresh();
    setNotice({ tone: 'success', text: t.notices.cancelled });
  }, [client, refresh, t]);

  const resumeSubscription = useCallback(async () => {
    await client.resumeSubscription();
    await refresh();
    setNotice({ tone: 'success', text: t.notices.resumed });
  }, [client, refresh, t]);

  useEffect(() => {
    void refresh();
    // Signing in and subscribing often finish in another tab, such as one opened from the email.
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') void refresh();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [refresh]);

  const resetsAt = account?.usage.resetsAt;
  useEffect(() => {
    if (!resetsAt) return;
    const delay = Math.max(0, Date.parse(resetsAt) - Date.now()) + RESET_GRACE_MS;
    const timer = setTimeout(() => void refresh(), delay);
    return () => {
      clearTimeout(timer);
    };
  }, [refresh, resetsAt]);

  const continueArrival = useEffectEvent(() => {
    // Development's strict mode runs effects twice; the second run finds nothing to do.
    if (!clearArrival()) return;
    if (arrival.checkout) {
      checkout(arrival.checkout).catch((error: unknown) => {
        setNotice({ tone: 'warning', text: describeError(error) });
      });
    }
  });

  useEffect(() => {
    continueArrival();
  }, []);

  const openTransactionCheckout = useEffectEvent((isCurrent: () => boolean) => {
    client
      .getCheckoutSettings()
      .then(({ paddle }) => {
        if (!isCurrent() || !paddle) return;
        return openCheckout(paddle, {
          successUrl: new URL('/?checkout=success', window.location.origin).href,
          onClosed: () => {
            const href = withoutTransaction(window.location.href);
            window.history.replaceState(window.history.state, '', href);
            setNotice({ tone: 'warning', text: t.notices.checkoutCancelled });
          },
        });
      })
      .catch((error: unknown) => {
        if (isCurrent()) setNotice({ tone: 'warning', text: describeError(error) });
      });
  });

  useEffect(() => {
    if (!transaction) return;
    // Development's strict mode runs effects twice; only the second opens checkout.
    let current = true;
    openTransactionCheckout(() => current);
    return () => {
      current = false;
    };
  }, [transaction]);

  useEffect(() => {
    if (!arrival.planChanged) return;
    const timers = RECHECK_PLAN_AFTER_MS.map((delay) => setTimeout(() => void refresh(), delay));
    return () => {
      timers.forEach(clearTimeout);
    };
  }, [arrival.planChanged, refresh]);

  useEffect(() => {
    if ((dialog !== 'plans' && !plansWanted) || plans) return;
    client.getPlans().then(
      ({ plans: loaded, planChangesEnabled: enabled }) => {
        setPlanChangesEnabled(enabled);
        setPlans(loaded);
      },
      (error: unknown) => {
        setPlansError(describeError(error));
      },
    );
  }, [client, describeError, dialog, plans, plansWanted]);

  // Signing in finishes in the tab opened from the email; once it shows up here, stop asking.
  const visibleDialog =
    (dialog === 'sign-in' && account?.user) ||
    (dialog === 'billing' && account && (!account.user || account.plan === 'free'))
      ? null
      : dialog;

  const value = useMemo<AccountContextValue>(
    () => ({
      client,
      account,
      plans,
      planChangesEnabled,
      plansError,
      customTones: customTones.tones,
      reloadCustomTones: customTones.reload,
      savedInstructions: savedInstructions.items,
      reloadSavedInstructions: savedInstructions.reload,
      instructionDraft,
      aiTools: aiTools.items,
      reloadAiTools: aiTools.reload,
      menuSettings: {
        tools: toolSettings.items,
        tones: toneSettings.items,
        instructions: instructionSettings.items,
      },
      reloadMenuSettings,
      keepMenuItem,
      newAiTool,
      newTone,
      dialog: visibleDialog,
      notice,
      openDialog: (next: AccountDialog, from?: PlansOpener) => {
        if (next === 'plans' && from) track({ event: 'plans', from });
        if (next === 'sign-in') track({ event: 'sign_in_opened' });
        setPlansError(null);
        setInstructionDraft(null);
        setNewAiTool(false);
        setNewTone(false);
        setDialog(next);
      },
      loadPlans: () => {
        setPlansWanted(true);
      },
      saveInstruction: (draft) => {
        setInstructionDraft(draft);
        setDialog('instructions');
      },
      makeAiTool: () => {
        setInstructionDraft(null);
        setNewTone(false);
        setNewAiTool(true);
        setDialog('ai-tools');
      },
      makeTone: () => {
        setInstructionDraft(null);
        setNewAiTool(false);
        setNewTone(true);
        setDialog('tones');
      },
      closeDialog: () => {
        setInstructionDraft(null);
        setNewAiTool(false);
        setNewTone(false);
        setDialog(null);
      },
      notify: setNotice,
      refresh,
      applyUsage,
      signOut: async () => {
        await client.signOut();
        await refresh();
      },
      checkout,
      changePlan,
      cancelSubscription,
      resumeSubscription,
    }),
    [
      account,
      aiTools.items,
      aiTools.reload,
      applyUsage,
      cancelSubscription,
      changePlan,
      checkout,
      client,
      customTones.reload,
      customTones.tones,
      instructionDraft,
      instructionSettings.items,
      keepMenuItem,
      newAiTool,
      newTone,
      notice,
      plans,
      planChangesEnabled,
      plansError,
      refresh,
      reloadMenuSettings,
      resumeSubscription,
      track,
      savedInstructions.items,
      savedInstructions.reload,
      toneSettings.items,
      toolSettings.items,
      visibleDialog,
    ],
  );

  return <AccountContext value={value}>{children}</AccountContext>;
}
